import { supabase } from '@/integrations/supabase/client';

/**
 * Datos del negocio capturados en el alta y transportados en
 * `auth.users.user_metadata.vendor_signup` hasta que exista una sesión
 * autenticada capaz de ejecutar la RPC `grant_vendor_role` (007 · TV-1).
 */
export interface VendorSignupData {
  name: string;
  slug: string;
  category: string;
  location: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  description: string | null;
}

export type ProvisionResult =
  | 'provisioned' // se acaba de conceder rol + perfil
  | 'already' //     ya tenía rol vendor (no-op)
  | 'not-vendor' //  autenticado pero sin rol ni alta pendiente
  | 'no-pending'; // sin sesión: nada que aprovisionar

/**
 * Genera el slug del vendor a partir del nombre del negocio, con sufijo
 * temporal en base36 para evitar colisiones. Se genera UNA vez en el alta
 * y se persiste en la metadata para que el aprovisionamiento sea determinista.
 */
export function generateVendorSlug(name: string): string {
  const base = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return `${base}-${Date.now().toString(36)}`;
}

/**
 * Completa el aprovisionamiento diferido del proveedor (007 · TV-1 / GAP-V1).
 *
 * Debe llamarse cuando ya existe sesión autenticada (primer login, magic link,
 * o aterrizaje en /vendor-auth tras confirmar el email). Idempotente: la RPC
 * `grant_vendor_role` ya es idempotente y aquí se limpia la marca tras éxito
 * para no reintentar.
 */
export async function ensureVendorProvisioned(): Promise<ProvisionResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return 'no-pending';

  // ¿Ya tiene el rol vendor? Entonces no hay nada que hacer.
  const { data: role } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .eq('role', 'vendor')
    .maybeSingle();
  if (role) return 'already';

  const pending = user.user_metadata?.vendor_signup as
    | VendorSignupData
    | null
    | undefined;
  if (!pending) return 'not-vendor';

  const { error } = await supabase.rpc('grant_vendor_role', {
    p_name: pending.name,
    p_slug: pending.slug,
    p_category: pending.category,
    p_location: pending.location,
    p_contact_phone: pending.contact_phone,
    p_contact_email: pending.contact_email,
    p_description: pending.description,
  });
  if (error) throw error;

  // Limpia la marca para que el aprovisionamiento corra exactamente una vez.
  await supabase.auth.updateUser({
    data: { vendor_signup: null, vendor_provisioned: true },
  });

  return 'provisioned';
}
